import { CircularProgress } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { createElement } from "react";
import { useTranslation } from "react-i18next";
import { TbCancel, TbDownload, TbLicense, TbLicenseOff } from "react-icons/tb";

import { downloadUetdsPdf } from "@/helpers/download";
import useBulkUETDS from "@/providers/hooks/useBulkUETDS";
import { useTrackingActions } from "@/stores/use-tracking-table";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import TrackingActionHeader from "./TrackingActionHeader";

export enum TrackingClickActionNames {
    UETDS_REGISTER = "UETDS_REGISTER",
    UETDS_CANCEL = "UETDS_CANCEL",
    UETDS_DOWNLOAD = "UETDS_DOWNLOAD",
    TASK_CANCEL = "TASK_CANCEL",
}

export const trackingActions: {
    label: string;
    icon: React.ElementType;
    color: string;
    showInMenu?: true;
    onClick: TrackingClickActionNames;
    mobile: boolean;
}[] = [
    {
        label: "UETDS Oluştur",
        icon: TbLicense,
        color: "primary" as const,
        onClick: TrackingClickActionNames.UETDS_REGISTER,
        mobile: true,
    },
    {
        label: "UETDS İndir",
        icon: TbDownload,
        color: "primary" as const,
        onClick: TrackingClickActionNames.UETDS_DOWNLOAD,
        mobile: false,
    },
    {
        label: "Görevi İptal Et",
        icon: TbCancel,
        color: "primary" as const,
        onClick: TrackingClickActionNames.TASK_CANCEL,
        mobile: true,
        showInMenu: true,
    },
    {
        label: "UETDS İptal Et",
        icon: TbLicenseOff,
        color: "error" as const,
        showInMenu: true,
        onClick: TrackingClickActionNames.UETDS_CANCEL,
        mobile: true,
    },
];

function TrackingActionsColumn({
    allRows,
}: {
    allRows: Task<IPlanPassenger<"api">>[];
}): GridColDef<Task<IPlanPassenger<"api">>> {
    return {
        field: "actions",
        type: "actions",
        width: 60,
        cellClassName: "!px-2",
        pinnable: true,
        filterable: false,
        editable: false,
        disableColumnMenu: true,

        renderHeader: () => <TrackingActionHeader allRows={allRows} />,
        getActions: (params) => {
            const { handleBulkUETDS, isPending } = useBulkUETDS();
            const { updateShowBulkCancelDialog, updateShowBulkUnassignDialog } = useTrackingActions();
            const { t } = useTranslation();

            const clickActions: Record<TrackingClickActionNames, { cb: () => void; cond: boolean }> = {
                [TrackingClickActionNames.UETDS_REGISTER]: {
                    cb: async () => handleBulkUETDS([params.row.id]),
                    cond: !params.row.uetdsId,
                },
                [TrackingClickActionNames.UETDS_DOWNLOAD]: {
                    cb: async () => downloadUetdsPdf(params.row.uetdsPdfUrl, params.row.id, t),
                    cond: !!params.row.uetdsId,
                },
                [TrackingClickActionNames.UETDS_CANCEL]: {
                    cb: async () => {
                        updateShowBulkCancelDialog({ ids: [params.row.id] });
                    },
                    cond: !!params.row.uetdsId,
                },
                [TrackingClickActionNames.TASK_CANCEL]: {
                    cb: async () => {
                        const ids = (params.row.passengers || []).map((passenger) => passenger.id);
                        if (!ids.length) return;
                        updateShowBulkUnassignDialog({
                            ids,
                            totalPassengers: ids.length,
                        });
                    },
                    cond: true,
                },
            };
            return trackingActions
                .filter((action) => clickActions[action.onClick].cond)
                .map((action) => (
                    <GridActionsCellItem
                        icon={createElement(isPending ? CircularProgress : action.icon, { size: 14 })}
                        label={action.label}
                        onClick={clickActions[action.onClick].cb}
                        color={action.color}
                        showInMenu={action.showInMenu as true}
                        key={action.label}
                        disabled={isPending}
                    />
                ));
        },
    };
}

export default TrackingActionsColumn;
